import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { emitOnce } from '@ngneat/elf';
import { useObservable } from '@ngneat/react-rxjs';
import { auditsInformationService } from '@store/audits/audits-information/audit-information.service';
import {
  AuditInformation,
  AuditInformationCreatedFromStepEnum, AuditInformationTypeEnum, RepositionDTO,
} from '@store/audits/audits-information/audits-information.model';
import { auditsInformationQuery } from '@store/audits/audits-information/audits-information.query';
import { templatesQuery } from '@store/audits/templates/templates.query';
import { templatesService } from '@store/audits/templates/templates.service';
import { sortByPosition } from '@utils/sortData';
import { useResponsive } from '@utils/useResponsive';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { finalize } from 'rxjs';
import AuditsContent from '../../components/AuditsContent';
import { useDragnDrop } from './utils/useDragnDrop';

interface PreAuditTabProps {
  isTemplate?: boolean;
}

const PreAuditTab = (props: PreAuditTabProps) => {
  const { isTemplate } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [{ auditTemplateDetails }] = useObservable(templatesQuery.auditTemplateDetails$);
  const { auditId } = useParams();
  const { templateId } = useParams();
  const [isloading, setIsloading] = useState<boolean>(false);
  const [isDiseabled, setIsDiseabled] = useState<boolean>(true);
  const [dragnDropItems, setDragnDropItems] = useState<AuditInformation[]>([]);
  const { onDragEnd, getListStyle } = useDragnDrop();
  const [{ auditsInformation }] = useObservable(auditsInformationQuery.auditsInformationList$);
  const { isTablets } = useResponsive();
  const { enqueueSnackbar } = useSnackbar();


  useEffect(() => {
    emitOnce(() => {
      auditsInformationService.setStep(AuditInformationCreatedFromStepEnum.PREAUDIT);
    });

    if (auditId) {
      setIsloading(true);
      auditsInformationService.getAuditsInformation(auditId).pipe(
        finalize(() => setTimeout(() => setIsloading(false), 100))
      ).subscribe();
    }
    if (templateId && isTemplate) {
      templatesService.getTemplateById(templateId).subscribe();
    }

    return () => {
      auditsInformationService.resetStore();
    };
  }, [auditId, isTemplate, templateId]);

  useEffect(() => {
    if (auditsInformation) {
      setDragnDropItems(auditsInformation.sort(sortByPosition));
    }
  }, [auditsInformation]);


  const handleSavePosition = () => {
    if (!isDiseabled && auditId) {
      const positionData: RepositionDTO = {
        data: dragnDropItems.map((item, idx) => ({ ressourceId: item.id, position: idx })),
      };
      auditsInformationService.updatePosition(positionData, auditId).subscribe();
    }
    setIsDiseabled(!isDiseabled);
  };

  const handleExportCSV = () => {
    auditsInformationService.getPreAuditCSV(auditId!)
        .pipe()
        .subscribe({
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
  };

  return (
    <Stack flex={1} width="100%" py={'20px'} spacing={2} sx={{ backgroundColor: 'white' }} mt='20px' borderRadius='2px' mb='20px'>
      <Stack direction="row" spacing={2} alignItems='center' justifyContent='space-between' px={'20px'}>
        <Typography fontSize="20px" fontWeight="700">
          {t('audits.global.pre-audit')}
        </Typography>
        {!isTemplate && (
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" color="primary" onClick={handleExportCSV}>
              {t('global.exportCSV')}
            </Button>
            <Button variant="outlined" color="primary" onClick={handleSavePosition}>
              {isDiseabled ? t('audits.buttons.dragnDrop') : t('audits.buttons.save-position')}
            </Button>
            <Button variant="contained" color="primary" onClick={() => { navigate('add-data'); }}>
              {t('audits.buttons.add-data')}
            </Button>
          </Stack>
        )}
      </Stack>
      <DragDropContext onDragEnd={(res) => onDragEnd(res, dragnDropItems, setDragnDropItems)}>
        <Stack
          overflow="auto"
          height={'15rem'}
          py={'15px'}
          px="20px"
          flexGrow={1}
          my="8px"
          spacing={2}
          className="scrollable"
          sx={{
            alignItems: isloading ? 'center' : 'none',
            justifyContent: isloading ? 'center' : 'none'
          }}
        >
          <Droppable droppableId="droppable">
            {(provided, snapshot) => (
              <Stack
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={getListStyle(snapshot.isDraggingOver)}
                sx={{ width: isTablets ? 'fit-content' : undefined }}
                spacing={2}
              >
                {isloading ?
                  <CircularProgress
                    size={20}
                    style={{ alignSelf: 'center' }}
                    thickness={10}
                  />
                  : !isTemplate && dragnDropItems ?
                    dragnDropItems.map((auditInformation, index) =>
                      <Draggable
                        key={auditInformation.id.toString()}
                        draggableId={auditInformation.id.toString()}
                        isDragDisabled={isDiseabled}
                        index={index}
                      >
                        {(provided) => (
                          <Stack
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            sx={{
                              border: !isDiseabled ? '1px dashed #ccc' : 'none',
                            }}
                          >
                            <AuditsContent auditInformation={auditInformation} isTemplate={isTemplate} isDraggable={isDiseabled} />
                          </Stack>
                        )}
                      </Draggable>
                    )
                    : <Typography color='#868F9C' fontSize="15px" fontWeight="100">
                      {!isTemplate ? t('audits.global.pre-audit-no-data') : null}
                    </Typography>
                }
                {auditTemplateDetails && isTemplate && auditTemplateDetails?.auditInformationPreAudit?.length !== 0 && (
                  auditTemplateDetails?.auditInformationPreAudit?.map((auditInformation, index) => {
                    const auditIn: AuditInformation = {
                      id: 0,
                      type: auditInformation as AuditInformationTypeEnum,
                      auditId: '0',
                      createdFrom: AuditInformationCreatedFromStepEnum.PREAUDIT
                    };
                    return (
                      <Draggable key={auditIn.id.toString()}
                        draggableId={auditIn.id.toString()}
                        index={index}
                      >
                        {(provided) => (
                          <AuditsContent
                            key={index}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            auditInformation={auditIn}
                            isTemplate={isTemplate} />
                        )}
                      </Draggable>
                    );
                  })
                )}
                {provided.placeholder}
              </Stack>
            )}
          </Droppable>
        </Stack>
      </DragDropContext>
      <Outlet />
    </Stack >
  );
};

export default PreAuditTab;
